import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Calendar, Sun } from 'lucide-react';

interface Props {
  show: boolean;
  currentDay: number;
  currentWeek: number;
  isRecipeCreationDay: boolean;
  isCustomerServingDay: boolean;
}

export function DayTransitionNotification({ show, currentDay, currentWeek, isRecipeCreationDay, isCustomerServingDay }: Props) {
  if (!show) return null;

  const getDayTypeInfo = () => {
    if (isRecipeCreationDay) {
      return {
        title: 'Recipe Creation Day',
        description: 'Time to experiment and discover new recipes!',
        color: 'from-purple-500 to-pink-500',
        icon: <Sun className="w-8 h-8" />
      };
    } else if (isCustomerServingDay) {
      return {
        title: 'Customer Service Day',
        description: 'Customers will be placing orders soon!',
        color: 'from-emerald-500 to-green-500',
        icon: <Calendar className="w-8 h-8" />
      };
    } else {
      return {
        title: 'New Day',
        description: 'Ready for another day in the kitchen?',
        color: 'from-blue-500 to-cyan-500',
        icon: <Sun className="w-8 h-8" />
      };
    }
  };

  const dayInfo = getDayTypeInfo();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        className="fixed inset-0 bg-slate-900/80 z-[3000] flex items-center justify-center"
      >
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -50, opacity: 0 }}
          className={`bg-gradient-to-r ${dayInfo.color} p-8 rounded-2xl shadow-2xl text-center text-white max-w-md mx-4`}
        >
          <div className="flex justify-center mb-4">
            {dayInfo.icon}
          </div>
          
          <h2 className="text-3xl font-bold mb-2">
            Week {currentWeek}, Day {currentDay}
          </h2>
          
          <h3 className="text-xl font-semibold mb-3">
            {dayInfo.title}
          </h3>
          
          <p className="text-lg opacity-90">
            {dayInfo.description}
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
